import { useContext, useEffect, useRef, useState } from "react";

import "./AlertsList.css";
import { useClickedOutside } from "../../utils/helpers";

import { JobContext, VersionContext } from "../../Context";
import { JobsStatus, jobStatusToString } from "../../constants/Jobs";

import SmallArrowIcon from "../../assets/alerts/small-arrow.svg?react";
import ArrowClockIcon from "../../assets/alerts/clock-arrow.svg?react";
import BriefcaseIcon from "../../assets/jobs/briefcase.svg?react";
import { useDispatch, useSelector } from "react-redux";
import { changeRightPanelTab } from "../../store/appSlice";
import { RightPanelTabs } from "../../constants/Panels";
import { isDefaultVersion } from "../../utils/versioningUtils";
import { StoreState } from "../../store/rootReducer";
import { AppDispatch } from "../../store/storeSetup";
import { SwitchToDefaultEventType, VersionIsDefaultEventType } from "../VersionControl/VersionControl";
import type Graphic from "@arcgis/core/Graphic";

interface AlertsListProps {
    qa1Alerts: Graphic[];
    qa2Alerts: Graphic[];
    controlDeviationAlerts: Graphic[];
    analysisFailedAlerts: Graphic[];
    rejectedPlanAlerts: Graphic[];
    expiredEfs: { [layerName: string]: Graphic[] };
    closeAlerts: () => void;
}

enum AlertType {
    QA1 = "QA1",
    QA2 = "QA2",
    REJECTED_PLAN = "Plan Ikke Godkjent",
    ANALYSIS_DEVIATION = "Droneskjerming Avvik",
    ANALYSIS_FAILED = "Dronevideo Analyse Feilet",
    ALL = "ALL",
}

const AlertsList = (props: AlertsListProps) => {
    const dispatch = useDispatch<AppDispatch>();

    const dropdownRef = useRef<HTMLDivElement>(null);
    const [typeDropdownOpen, setTypeDropdownOpen] = useState<boolean>(false);
    const [alertTypeToShow, setAlertTypeToShow] = useState<AlertType>(AlertType.ALL);

    const [sortedAlertsWeek, setSortedAlertsWeek] = useState<Graphic[]>([]);
    const [sortedAlertsOlder, setSortedAlertsOlder] = useState<Graphic[]>([]);

    const jobsContext = useContext(JobContext);

    const versionContext = useContext(VersionContext);
    const versionServiceRef = versionContext?.versionServiceRef.value;
    const activeVersion = useSelector((state: StoreState) => state.version.versionInfo);
    const checkIfDefaultVersion = () => {
        if (activeVersion && versionServiceRef?.current) {
            return isDefaultVersion(activeVersion, versionServiceRef?.current);
        }
        return false;
    };

    const handleAlertTypeChange = (type: AlertType, event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        setAlertTypeToShow(type);
        setTypeDropdownOpen(false);
    };

    const openAlert = (alert: Graphic) => {
        let shouldSwitch = false;
        if (!checkIfDefaultVersion()) {
            if (alert.attributes.status === JobsStatus.QA1 || alert.attributes.status === JobsStatus.QA2) {
                if (globalThis.confirm("Du står ikke i Planroot. Skal vi bytte til Planroot før vi åpner jobben?")) {
                    shouldSwitch = true;
                    globalThis.dispatchEvent(new CustomEvent(SwitchToDefaultEventType));
                }
            }
        }
        if (shouldSwitch) {
            const switchVersionListener = () => {
                jobsContext?.selectedJob.set(alert);
                dispatch(changeRightPanelTab(RightPanelTabs.JOBS));
                props.closeAlerts();
                globalThis.removeEventListener(VersionIsDefaultEventType, switchVersionListener);
            };
            globalThis.addEventListener(VersionIsDefaultEventType, switchVersionListener);
        } else {
            jobsContext?.selectedJob.set(alert);
            dispatch(changeRightPanelTab(RightPanelTabs.JOBS));
            props.closeAlerts();
        }
    };

    const openJobsPanel = () => {
        const result = dispatch(changeRightPanelTab(RightPanelTabs.JOBS));
        if (!result) {
            // User cancelled, don't unselect
            return;
        }
        props.closeAlerts();
    };

    const getExpiredCount = () => {
        return Object.values(props.expiredEfs).reduce((count, efsObjects) => {
            return count + efsObjects.length;
        }, 0);
    };

    const openEfsManager = () => {
        const result = dispatch(changeRightPanelTab(RightPanelTabs.EFS_MANAGER));
        if (!result) {
            // User cancelled, don't unselect
            return;
        }
        props.closeAlerts();
    };

    useClickedOutside(dropdownRef, () => {
        if (typeDropdownOpen) {
            setTypeDropdownOpen(false);
        }
    });

    useEffect(() => {
        let tempSortedAlerts: Graphic[] = [];
        switch (alertTypeToShow) {
            case AlertType.QA1:
                tempSortedAlerts = [...props.qa1Alerts];
                break;
            case AlertType.QA2:
                tempSortedAlerts = [...props.qa2Alerts];
                break;
            case AlertType.REJECTED_PLAN:
                tempSortedAlerts = [...props.rejectedPlanAlerts];
                break;
            case AlertType.ANALYSIS_DEVIATION:
                tempSortedAlerts = [...props.controlDeviationAlerts];
                break;
            case AlertType.ANALYSIS_FAILED:
                tempSortedAlerts = [...props.analysisFailedAlerts];
                break;
            case AlertType.ALL:
            default:
                tempSortedAlerts = [
                    ...props.qa1Alerts,
                    ...props.qa2Alerts,
                    ...props.rejectedPlanAlerts,
                    ...props.controlDeviationAlerts,
                    ...props.analysisFailedAlerts,
                ];
                break;
        }
        tempSortedAlerts.sort((a, b) => {
            const aDate = new Date(a.attributes.oppdatert_dato).getTime();
            const bDate = new Date(b.attributes.oppdatert_dato).getTime();
            return bDate - aDate;
        });

        const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
        const filteredAlerts = tempSortedAlerts.filter(
            (alert) => new Date(alert.attributes.oppdatert_dato) >= sevenDaysAgo
        );
        const olderAlerts = tempSortedAlerts.filter(
            (alert) => new Date(alert.attributes.oppdatert_dato) < sevenDaysAgo
        );
        setSortedAlertsWeek(filteredAlerts);
        setSortedAlertsOlder(olderAlerts);
    }, [alertTypeToShow, props.qa1Alerts, props.qa2Alerts, props.rejectedPlanAlerts]);

    return (
        <div className="alerts-list">
            <div className="alerts-list-header">
                <div ref={dropdownRef}>
                    <button
                        className="alerts-list-header-dropdown-button"
                        onClick={() => setTypeDropdownOpen(!typeDropdownOpen)}
                    >
                        <span>{alertTypeToShow}</span>
                        <SmallArrowIcon />
                    </button>
                    {typeDropdownOpen && (
                        <div
                            className="alerts-list-header-dropdown-options"
                            style={{ position: "absolute", display: "flex", flexDirection: "column" }}
                        >
                            {Object.values(AlertType).map((type) => (
                                <button
                                    key={type}
                                    className="alerts-list-header-dropdown-option"
                                    onClick={(e) => handleAlertTypeChange(type, e)}
                                >
                                    {type}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>
            <div className="alerts-list-content">
                <div className="alert-group">
                    <div className="alert-group-header">Siste 7 dager</div>
                    {sortedAlertsWeek.length === 0 && (
                        <div className="no-alerts">Ingen jobber på vent fra siste 7 dager</div>
                    )}
                    {sortedAlertsWeek.map((alert) => (
                        <button
                            key={alert.attributes.objectid}
                            onClick={() => openAlert(alert)}
                            className="alert-item-container"
                        >
                            <BriefcaseIcon />
                            <div className="alert-item-content">
                                <span className="alert-item-content-header">
                                    <span className="alert-item-content-header-title">{alert.attributes.navn}</span>
                                    <span className="alert-item-content-header-date">
                                        {new Date(alert.attributes.oppdatert_dato).toLocaleDateString("no-NO")}
                                    </span>
                                </span>
                                <div className="alert-item-description">{alert.attributes.beskrivelse}</div>
                                <div>Eier: {alert.attributes.opprettet_av}</div>
                                <div className="alert-item-status-chip">
                                    <ArrowClockIcon />
                                    <span>{jobStatusToString(alert.attributes.status)}</span>
                                    <span style={{ width: "24px" }}></span>
                                </div>
                            </div>
                        </button>
                    ))}
                </div>
                <div className="alert-group">
                    <div className="alert-group-header">Eldre</div>
                    {sortedAlertsOlder.length === 0 && <div className="no-alerts">Ingen eldre jobber på vent</div>}
                    {sortedAlertsOlder.map((alert) => (
                        <button
                            key={alert.attributes.objectid}
                            onClick={() => openAlert(alert)}
                            className="alert-item-container"
                        >
                            <BriefcaseIcon />
                            <div className="alert-item-content">
                                <span className="alert-item-content-header">
                                    <span className="alert-item-content-header-title">{alert.attributes.navn}</span>
                                    <span className="alert-item-content-header-date">
                                        {new Date(alert.attributes.oppdatert_dato).toLocaleDateString("no-NO")}
                                    </span>
                                </span>
                                <div className="alert-item-description">{alert.attributes.beskrivelse}</div>
                                <div>Eier: {alert.attributes.opprettet_av}</div>
                                <div className="alert-item-status-chip">
                                    <ArrowClockIcon />
                                    <span>{jobStatusToString(alert.attributes.status)}</span>
                                    <span style={{ width: "24px" }}></span>
                                </div>
                            </div>
                        </button>
                    ))}
                </div>
            </div>
            <div className="flex-row center">
                <button className="text-button" onClick={openJobsPanel}>
                    Åpne alle jobber
                </button>
            </div>
            {checkIfDefaultVersion() && (
                <div className="alert-group alerts-list-top-border">
                    <span>Antall EFS-meldinger over tiden: {getExpiredCount()}</span>

                    <span className="flex-row center w-100">
                        <button className="text-button" onClick={openEfsManager}>
                            Åpne EFS-administrasjon
                        </button>
                    </span>
                </div>
            )}
        </div>
    );
};

export default AlertsList;
